import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatIDR, formatDate, MONTHS_ID } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, Loader2, Wallet, TrendingDown, FileText, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CATEGORIES = [
  "Gaji Staff",
  "Listrik & Air",
  "Internet & Telekomunikasi",
  "Supplies & Amenities",
  "Maintenance & Perbaikan",
  "Marketing",
  "Pajak & Perizinan",
  "Lainnya",
];

const CATEGORY_COLORS = {
  "Gaji Staff": "bg-rose-100 text-rose-800",
  "Listrik & Air": "bg-amber-100 text-amber-800",
  "Internet & Telekomunikasi": "bg-cyan-100 text-cyan-800",
  "Supplies & Amenities": "bg-emerald-100 text-emerald-800",
  "Maintenance & Perbaikan": "bg-orange-100 text-orange-800",
  "Marketing": "bg-violet-100 text-violet-800",
  "Pajak & Perizinan": "bg-slate-200 text-slate-800",
  "Lainnya": "bg-stone-200 text-stone-800",
};

const today = () => new Date().toISOString().slice(0, 10);
const currentYear = new Date().getFullYear();

const empty = {
  date: today(),
  category: "Lainnya",
  description: "",
  amount: 0,
  notes: "",
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState("all");
  const [filterCat, setFilterCat] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("year", year);
      if (month !== "all") params.set("month", month);
      if (filterCat !== "all") params.set("category", filterCat);

      const sumParams = new URLSearchParams();
      sumParams.set("year", year);
      if (month !== "all") sumParams.set("month", month);

      const [listRes, sumRes] = await Promise.all([
        api.get(`/expenses?${params.toString()}`),
        api.get(`/expenses/summary?${sumParams.toString()}`),
      ]);
      setExpenses(listRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error("Gagal memuat pengeluaran");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, month, filterCat]); // eslint-disable-line

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, date: today() });
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date,
      category: e.category,
      description: e.description,
      amount: e.amount,
      notes: e.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.description.trim()) {
      toast.error("Deskripsi wajib diisi");
      return;
    }
    if (Number(form.amount) <= 0) {
      toast.error("Jumlah harus lebih dari 0");
      return;
    }
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Pengeluaran diperbarui");
      } else {
        await api.post("/expenses", payload);
        toast.success("Pengeluaran dicatat");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/expenses/${deleteId}`);
      toast.success("Pengeluaran dihapus");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Gagal menghapus");
    }
  };

  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) arr.push(String(y));
    return arr;
  }, []);

  const topCategories = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.by_category || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
  }, [summary]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pengeluaran"
        subtitle="Catat semua biaya operasional homestay. Laporan profit otomatis = Revenue − Expenses."
        actions={
          <div className="flex items-center gap-2">
            <Link
              data-testid="btn-print-expenses-link"
              to={`/expenses/print/${year}${month !== "all" ? `/${month}` : ""}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Cetak laporan ${month !== "all" ? MONTHS_ID[Number(month) - 1] : ""} ${year}`.trim()}
            >
              <Button
                variant="outline"
                className="border-[#EBE6DE] text-[#172E21] hover:bg-[#FDFBF7] rounded-xl"
              >
                <Printer className="h-4 w-4 mr-2" />
                Cetak Laporan
              </Button>
            </Link>
            <Button
              data-testid="btn-new-expense"
              onClick={openNew}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
            >
              <Plus className="h-4 w-4 mr-2" />
              Catat Pengeluaran
            </Button>
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <SummaryCard
          icon={Wallet}
          label="Total Pengeluaran"
          value={formatIDR(summary?.total || 0)}
          subtitle={`${summary?.count || 0} transaksi`}
          tone="terracotta"
          testid="summary-total-expenses"
        />
        {topCategories.map(([cat, amt], idx) => (
          <SummaryCard
            key={cat}
            icon={TrendingDown}
            label={`#${idx + 1} ${cat}`}
            value={formatIDR(amt)}
            subtitle={`${((amt / (summary?.total || 1)) * 100).toFixed(0)}% dari total`}
            testid={`summary-cat-${idx}`}
          />
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#EBE6DE] p-4 flex flex-wrap gap-3 items-center shadow-sm">
        <div className="text-sm font-medium text-[#172E21]">Filter:</div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger data-testid="filter-year" className="w-32 border-[#EBE6DE]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger data-testid="filter-month" className="w-44 border-[#EBE6DE]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Bulan</SelectItem>
            {MONTHS_ID.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger data-testid="filter-category" className="w-56 border-[#EBE6DE]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#EBE6DE] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="expenses-table">
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead className="font-bold text-[#172E21]">Tanggal</TableHead>
                <TableHead className="font-bold text-[#172E21]">Kategori</TableHead>
                <TableHead className="font-bold text-[#172E21]">Deskripsi</TableHead>
                <TableHead className="font-bold text-[#172E21] text-right">Jumlah</TableHead>
                <TableHead className="font-bold text-[#172E21] text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#D16B4D] inline" /></TableCell></TableRow>
              )}
              {!loading && expenses.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-[#586D61] py-10">
                  Belum ada pengeluaran untuk periode ini. Klik "Catat Pengeluaran".
                </TableCell></TableRow>
              )}
              {[...expenses].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-[#FDFBF7]">
                  <TableCell className="font-mono text-sm">{formatDate(e.date)}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${CATEGORY_COLORS[e.category] || "bg-stone-200 text-stone-800"}`}>
                      {e.category}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-[#172E21]">
                    <div className="font-medium">{e.description}</div>
                    {e.notes && <div className="text-xs text-[#586D61] mt-0.5 truncate max-w-md" title={e.notes}>{e.notes}</div>}
                  </TableCell>
                  <TableCell className="text-right font-bold text-[#D16B4D] font-mono">{formatIDR(e.amount)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        data-testid={`btn-edit-expense-${e.id}`}
                        variant="ghost" size="icon"
                        onClick={() => openEdit(e)}
                        className="h-8 w-8 hover:bg-[#2C7A51]/10 hover:text-[#2C7A51]"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`btn-delete-expense-${e.id}`}
                        variant="ghost" size="icon"
                        onClick={() => setDeleteId(e.id)}
                        className="h-8 w-8 hover:bg-[#D14D4D]/10 hover:text-[#D14D4D]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl bg-white" data-testid="expense-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#172E21]">
              {editing ? "Edit Pengeluaran" : "Catat Pengeluaran Baru"}
            </DialogTitle>
            <DialogDescription className="text-[#586D61]">
              Isi detail pengeluaran operasional.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div>
              <Label className="text-sm font-medium">Tanggal</Label>
              <Input
                data-testid="input-expense-date"
                type="date"
                value={form.date}
                onChange={(ev) => setForm({ ...form, date: ev.target.value })}
                className="mt-1.5 border-[#EBE6DE]"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="select-expense-category" className="mt-1.5 border-[#EBE6DE]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium">Deskripsi</Label>
              <Input
                data-testid="input-expense-description"
                value={form.description}
                onChange={(ev) => setForm({ ...form, description: ev.target.value })}
                placeholder="Mis: Listrik PLN bulan Feb"
                className="mt-1.5 border-[#EBE6DE]"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium">Jumlah (IDR)</Label>
              <Input
                data-testid="input-expense-amount"
                type="number" min={0} step={1000}
                value={form.amount}
                onChange={(ev) => setForm({ ...form, amount: ev.target.value })}
                className="mt-1.5 border-[#EBE6DE] font-mono"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium">Catatan (opsional)</Label>
              <Textarea
                data-testid="input-expense-notes"
                value={form.notes}
                onChange={(ev) => setForm({ ...form, notes: ev.target.value })}
                placeholder="Detail tambahan, no. invoice, vendor, dll."
                className="mt-1.5 border-[#EBE6DE]"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl" data-testid="btn-cancel-expense">
              Batal
            </Button>
            <Button
              data-testid="btn-save-expense"
              onClick={save}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
            >
              {editing ? "Simpan Perubahan" : "Catat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Hapus Pengeluaran?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#586D61]">
              Data tidak dapat dikembalikan setelah dihapus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete-expense"
              onClick={doDelete}
              className="bg-[#D14D4D] hover:bg-[#B83E3E] text-white rounded-xl"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, subtitle, tone, testid }) {
  const toneCls =
    tone === "terracotta"
      ? "bg-[#D16B4D] text-white border-[#D16B4D]"
      : "bg-white text-[#172E21] border-[#EBE6DE]";
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 shadow-sm ${toneCls}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" />
        <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 truncate">{label}</div>
      </div>
      <div className="font-heading text-2xl font-semibold">{value}</div>
      <div className="text-xs opacity-70 mt-1">{subtitle}</div>
    </div>
  );
}
